import React, { useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import { motion } from 'framer-motion';
import { StarIcon, MinusIcon, PlusIcon, TruckIcon, RecycleIcon, ArrowRightIcon } from 'lucide-react';
import { getProduct, getRange, relatedProducts, sizes, rangePath } from '../data/mockData';
import { ProductCard } from '../components/ProductCard';
import { SectionHeading } from '../components/SectionHeading';
import { Accordion } from '../components/Accordion';
const EASE = [0.23, 1, 0.32, 1] as const;
const REVIEWS = [{
  name: 'Thandi M.',
  location: 'Cape Town',
  rating: 5,
  title: 'Two years in, still perfect',
  body: 'Used every single working day since 2023. The lid still seals and the print has not faded once.'
}, {
  name: 'Johan R.',
  location: 'Pretoria',
  rating: 5,
  title: 'The size is right',
  body: 'Fits the car cup holder and my usual order exactly. No spills on the N1.'
}, {
  name: 'Lerato K.',
  location: 'Johannesburg',
  rating: 4,
  title: 'Beautiful, slightly snug lid',
  body: 'Gorgeous thing. The lid takes a firm push the first few times, then it loosens up.'
}];
export function Product() {
  const {
    id
  } = useParams();
  const product = getProduct(id);
  const [activeImage, setActiveImage] = useState(0);
  const [qty, setQty] = useState(1);
  const [selectedSize, setSelectedSize] = useState(product?.size ?? '350ml');
  if (!product) {
    return <div className="mx-auto flex max-w-xl flex-col items-center px-6 py-40 text-center" data-id="element-649" data-mp="3vn0bzp4u1t3m">
        <p className="type-display text-4xl text-coffee" data-id="element-650" data-mp="3vpd0ym90d3q6">
          That cup has moved on.
        </p>
        <Link to="/shop" className="type-cta mt-8 rounded-full bg-coffee px-8 py-4 text-[11px] text-paper" data-id="element-651" data-mp="3loua37j10myr">
          Back to shop
        </Link>
      </div>;
  }
  const range = getRange(product.rangeId);
  const isStory = product.rangeKind === 'story';
  const sizeInfo = sizes.find(s => s.id === product.size);
  const gallery = [product.image, product.hoverImage, ...(range?.gallery ?? [])].slice(0, 4);
  const related = relatedProducts(product, 4);
  return <div className="w-full bg-paper" data-id="element-652" data-mp="3j67kgkwicxhc">
      {/* Breadcrumb */}
      <div className="mx-auto flex max-w-[1400px] flex-wrap items-center gap-2 px-6 py-6" data-id="element-653" data-mp="34h347999x3bv">
        <Link to="/shop" className="type-body text-xs text-ink/45 transition-colors hover:text-coffee" data-id="element-654" data-mp="39fd1bpek6e04">
          Shop
        </Link>
        <span className="text-ink/25" data-id="element-655" data-mp="5yecl206a1fb">/</span>
        <span className="type-eyebrow text-[9px] text-pennant" data-id="element-656" data-mp="5xcq81dphsia">
          {isStory ? 'Choose Your Story' : 'Collection'}
        </span>
        <span className="text-ink/25" data-id="element-657" data-mp="3s52s81f7afmr">/</span>
        {range && <Link to={rangePath(range)} className="type-body text-xs text-ink/45 transition-colors hover:text-coffee" data-id="element-658" data-mp="3s39mcocevzft">
            {range.name}
          </Link>}
        <span className="text-ink/25" data-id="element-659" data-mp="2gr8u2zgc9svb">/</span>
        <span className="type-body text-xs text-coffee" data-id="element-660" data-mp="295vg0m240r4x">{product.name}</span>
      </div>

      {/* Gallery + buy box */}
      <section className="mx-auto max-w-[1400px] px-6 pb-20" data-id="element-661" data-mp="246a83m4yp8g2">
        <div className="grid gap-12 lg:grid-cols-[1.15fr_1fr] lg:gap-20" data-id="element-662" data-mp="2j4axdxbc4m18">
          <div data-id="element-663" data-mp="2j2hyx5avl84b">
            <motion.div key={activeImage} initial={{
            opacity: 0
          }} animate={{
            opacity: 1
          }} transition={{
            duration: 0.25,
            ease: EASE
          }} className="overflow-hidden rounded-4xl bg-paper-warm" data-id="element-664" data-mp="2e5ht8vhxu7dq">
              <img src={gallery[activeImage]} alt={product.name} className="aspect-[4/5] w-full object-cover" data-id="element-665" data-mp="1lepq1e2ggigw" />
            </motion.div>
            <div className="mt-4 grid grid-cols-4 gap-4" data-id="element-666" data-mp="1vdoxwufeca2u">
              {gallery.map((src, i) => <button key={src + i} onClick={() => setActiveImage(i)} aria-label={`View image ${i + 1}`} className={`overflow-hidden rounded-2xl transition-[border-color] duration-200 ${activeImage === i ? 'ring-2 ring-coffee' : 'ring-1 ring-oat/60 hover:ring-coffee/40'}`} data-id="element-667" data-mp="17nb7f8cgmjad" data-mpi={i}>
                  <img src={src} alt="" className="aspect-square w-full object-cover" data-id="element-668" data-mp="jqm29hpr68au" />
                </button>)}
            </div>
          </div>

          {/* Buy box */}
          <div className="lg:pt-6" data-id="element-669" data-mp="xq3xofxk0jt6">
            {range && <Link to={rangePath(range)} className="type-eyebrow text-[10px] text-pennant hover:underline" data-id="element-670" data-mp="xqn2qw23vdps">
                {isStory ? 'Choose Your Story' : 'Collection'} · {range.name}
              </Link>}
            <h1 className="type-display mt-4 text-5xl text-coffee md:text-6xl" data-id="element-671" data-mp="34frzkf4px7q1">
              {product.name}
            </h1>

            <div className="mt-5 flex items-center gap-3" data-id="element-672" data-mp="2wyn29r49lecc">
              <Stars rating={product.rating} data-id="element-673" data-mp="2s5typ6kj8c1u" />
              <span className="type-body text-xs text-ink/50" data-id="element-674" data-mp="2s50zqrrtequ9">
                {product.rating} · {product.reviewCount} reviews
              </span>
            </div>

            <p className="type-display mt-7 text-3xl text-coffee" data-id="element-675" data-mp="2i8xpszqq1nwr">
              {product.price}
            </p>
            <p className="type-body mt-5 max-w-md text-base text-ink/70" data-id="element-676" data-mp="3fulkng7j3y66">
              {product.description}
            </p>

            {/* Colourway */}
            <div className="mt-9" data-id="element-677" data-mp="35xguz6eh5kkm">
              <p className="type-eyebrow mb-3 text-[10px] text-coffee/55" data-id="element-678" data-mp="35wpcmx9w1jca">
                Colourway
              </p>
              <div className="flex gap-3" data-id="element-679" data-mp="31vlta0smt56b">
                {product.colorways.map((c, i) => <button key={c} aria-label={`Colourway ${i + 1}`} className={`h-9 w-9 rounded-full ring-1 ring-inset ring-coffee/15 transition-transform duration-150 hover:scale-105 ${i === 0 ? 'ring-2 ring-coffee' : ''}`} style={{
                backgroundColor: c
              }} data-id="element-680" data-mp="1uk4xdd9b5y6w" data-mpi={i} />)}
              </div>
            </div>

            {/* Size */}
            <div className="mt-8" data-id="element-681" data-mp="1my17g3kwsytp">
              <div className="mb-3 flex items-center justify-between" data-id="element-682" data-mp="1n15hhhl22osa">
                <p className="type-eyebrow text-[10px] text-coffee/55" data-id="element-683" data-mp="196w34o6ljid1">Size</p>
                <Link to="/size-guide" className="type-body text-xs text-pennant underline underline-offset-4" data-id="element-684" data-mp="1dymdgu5ja64g">
                  Not sure? Size guide
                </Link>
              </div>
              <div className="grid grid-cols-3 gap-3" data-id="element-685" data-mp="146gprwmbeav2">
                {sizes.map(s => <button key={s.id} onClick={() => setSelectedSize(s.id)} className={`rounded-2xl border px-4 py-4 text-left transition-colors ${selectedSize === s.id ? 'border-coffee bg-coffee text-paper' : 'border-oat text-coffee hover:border-coffee'}`} data-id="element-686" data-mp="q9lbnpr7ht85">
                    <span className="type-ui block text-sm" data-id="element-687" data-mp="fnoia8aj54tt">{s.label}</span>
                    <span className={`type-body block text-[11px] ${selectedSize === s.id ? 'text-paper/60' : 'text-ink/45'}`} data-id="element-688" data-mp="fpstlbard644">
                      {s.imperial}
                    </span>
                  </button>)}
              </div>
            </div>

            {/* Qty + add */}
            <div className="mt-8 flex flex-wrap items-center gap-4" data-id="element-689" data-mp="zcgxuekhsreq">
              <div className="flex items-center gap-5 rounded-full border border-oat px-5 py-3.5" data-id="element-690" data-mp="1dc8aaavd1zqm">
                <button onClick={() => setQty(Math.max(1, qty - 1))} aria-label="Decrease quantity" className="text-coffee/60 transition-colors hover:text-coffee" data-id="element-691" data-mp="18dome4x7idvm">
                  <MinusIcon className="h-3.5 w-3.5" strokeWidth={2} data-id="element-692" data-mp="2jshcauz245a7" />
                </button>
                <span className="type-ui w-4 text-center text-sm text-coffee" data-id="element-693" data-mp="2onek5yklo8li">
                  {qty}
                </span>
                <button onClick={() => setQty(qty + 1)} aria-label="Increase quantity" className="text-coffee/60 transition-colors hover:text-coffee" data-id="element-694" data-mp="1w13mavodolse">
                  <PlusIcon className="h-3.5 w-3.5" strokeWidth={2} data-id="element-695" data-mp="25yq2dlheyjdy" />
                </button>
              </div>
              <button className="type-cta flex-1 rounded-full bg-coffee px-9 py-4 text-[11px] text-paper transition-colors hover:bg-pennant" data-id="element-696" data-mp="2aqrl9q9378cy">
                Add to bag — {product.price}
              </button>
            </div>

            <div className="mt-7 space-y-2.5" data-id="element-697" data-mp="1i7km557k9p34">
              <p className="type-body flex items-center gap-2.5 text-xs text-ink/55" data-id="element-698" data-mp="1aqfscr6bgdu6">
                <TruckIcon className="h-4 w-4 text-coffee/50" strokeWidth={1.5} data-id="element-699" data-mp="15r2wjgxgvwfg" />
                Free nationwide delivery over R 750 · 2–4 working days
              </p>
              <p className="type-body flex items-center gap-2.5 text-xs text-ink/55" data-id="element-700" data-mp="10tt926tn1uu9">
                <RecycleIcon className="h-4 w-4 text-forest" strokeWidth={1.5} data-id="element-701" data-mp="vsdpkj73zwdz" />
                Bamboo fibre body · replaceable lid and sleeve
              </p>
            </div>

            {/* Detail accordions */}
            <div className="mt-10" data-id="element-702" data-mp="i2a6g13j0isr">
              <Accordion items={[{
              q: 'Size & fit',
              a: sizeInfo ? `${sizeInfo.label} (${sizeInfo.imperial}). ${sizeInfo.suits} Best for ${sizeInfo.drinks.join(', ').toLowerCase()}.` : 'Three sizes: 250ml, 350ml and 400ml.'
            }, {
              q: 'Materials',
              a: 'Natural bamboo fibre body with a food-grade silicone lid and sleeve. BPA free, no melamine, no phthalates.'
            }, {
              q: 'Care',
              a: 'Dishwasher safe on the top shelf. Let the lid dry separately. Never microwave.'
            }, {
              q: 'Delivery & returns',
              a: 'Flat-rate nationwide courier, free over R 750. Unused cups can be returned within 30 days.'
            }]} data-id="element-703" data-mp="hz4iwqajpoz5" />
            </div>
          </div>
        </div>
      </section>

      {/* Parent story */}
      {range && <section className="w-full bg-paper-warm py-20 md:py-28" data-id="element-704" data-mp="3go98j9lyzroe">
          <div className="mx-auto grid max-w-[1400px] items-center gap-12 px-6 lg:grid-cols-2 lg:gap-20" data-id="element-705" data-mp="zwhhbuze2jwz">
            <div className="overflow-hidden rounded-4xl" data-id="element-706" data-mp="vv5s3qkfg0pb">
              <img src={range.heroImage} alt={range.name} className="aspect-[4/3] w-full object-cover" data-id="element-707" data-mp="13fq3pf6qcz3d" />
            </div>
            <div data-id="element-708" data-mp="thaclc7pdptv">
              <p className="type-eyebrow mb-6 text-[11px] text-pennant" data-id="element-709" data-mp="thshsyccfedx">
                {isStory ? 'Choose Your Story' : 'The Collection'}
              </p>
              <h2 className="type-display text-4xl text-coffee md:text-5xl" data-id="element-710" data-mp="yig6hdd3nj4z">
                {range.name}
              </h2>
              <p className="type-body mt-6 max-w-lg text-base text-ink/70" data-id="element-711" data-mp="1r51774fgt7l7">
                {range.story}
              </p>
              <Link to={rangePath(range)} className="type-cta mt-8 inline-flex items-center gap-2 rounded-full bg-coffee px-8 py-4 text-[11px] text-paper transition-colors hover:bg-pennant" data-id="element-712" data-mp="1cgwo3zci2u1w">
                {isStory ? 'Discover the story' : 'Explore the collection'}
                <ArrowRightIcon className="h-3.5 w-3.5" strokeWidth={1.5} data-id="element-713" data-mp="29wk8n6tucdl4" />
              </Link>
            </div>
          </div>
        </section>}

      {/* Reviews */}
      <section className="mx-auto max-w-[1400px] px-6 py-20 md:py-28" data-id="element-714" data-mp="2erwhulhy08nl">
        <div className="grid gap-12 lg:grid-cols-[0.7fr_1.3fr] lg:gap-20" data-id="element-715" data-mp="2eqlfmq45b8yl">
          <div data-id="element-716" data-mp="2aoztsf9mvkc9">
            <p className="type-eyebrow mb-6 text-[11px] text-pennant" data-id="element-717" data-mp="2asno3gmwkas1">
              Reviews
            </p>
            <p className="type-display text-6xl text-coffee" data-id="element-718" data-mp="20sn03he396hi">
              {product.rating}
              <span className="text-2xl text-ink/35" data-id="element-719" data-mp="20uhpb7we7kre">/5</span>
            </p>
            <div className="mt-3" data-id="element-720" data-mp="1w0lu2d7ikbzx">
              <Stars rating={product.rating} data-id="element-721" data-mp="1r2kalr66eanx" />
            </div>
            <p className="type-body mt-4 text-sm text-ink/55" data-id="element-722" data-mp="1r33g28en36l2">
              Based on {product.reviewCount} verified reviews.
            </p>
          </div>
          <div className="grid gap-6 sm:grid-cols-3" data-id="element-723" data-mp="1i2f3li4w9eaa">
            {REVIEWS.map(r => <article key={r.name} className="flex h-full flex-col rounded-3xl border border-oat/70 bg-paper-warm p-7" data-id="element-724" data-mp="1d2izliw75muq">
                <Stars rating={r.rating} small data-id="element-725" data-mp="3oss3fyu62zcz" />
                <h3 className="type-ui mt-4 text-base text-coffee" data-id="element-726" data-mp="3tofxarjlggot">
                  {r.title}
                </h3>
                <p className="type-body mt-3 text-sm text-ink/65" data-id="element-727" data-mp="2jphfuf0dmio">{r.body}</p>
                <p className="type-eyebrow mt-auto pt-6 text-[9px] text-coffee/45" data-id="element-728" data-mp="6jrlga87us14">
                  {r.name} · {r.location}
                </p>
              </article>)}
          </div>
        </div>
      </section>

      {/* Related */}
      <section className="w-full bg-paper-warm py-20 md:py-28" data-id="element-729" data-mp="3awn16o98afcv">
        <div className="mx-auto max-w-[1400px] px-6" data-id="element-730" data-mp="3ezt7fvwcc6jc">
          <SectionHeading eyebrow="You may also like" title="Related cups" data-id="element-731" data-mp="3jr2ucoqi8vrj" />
          <div className="mt-14 grid gap-8 sm:grid-cols-2 lg:grid-cols-4" data-id="element-732" data-mp="3jt5tpzdjs18x">
            {related.map((p, i) => <ProductCard key={p.id} product={p} index={i} data-id="element-733" data-mp="2rb0du636yarw" data-mpi={i} />)}
          </div>
        </div>
      </section>
    </div>;
}
function Stars({
  rating,
  small
}: {
  rating: number;
  small?: boolean;
}) {
  const size = small ? 'h-3 w-3' : 'h-4 w-4';
  return <div className="flex gap-0.5" aria-label={`${rating} out of 5 stars`} data-id="element-734" data-mp="23957e4nq2fuj">
      {[1, 2, 3, 4, 5].map(n => <StarIcon key={n} className={`${size} ${n <= Math.round(rating) ? 'fill-radiant-yellow text-radiant-yellow' : 'text-oat'}`} strokeWidth={1.5} data-id="element-735" data-mp="2hyb2bgfj1jwm" />)}
    </div>;
}